import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, useFetch, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

export function visitorId() {
  let v = localStorage.getItem("pn_visitor");
  if (!v) { v = (crypto.randomUUID?.() || `${Date.now()}${Math.random()}`).replace(/[^A-Za-z0-9]/g, "").slice(0, 32); localStorage.setItem("pn_visitor", v); }
  return v;
}

const dayLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

function Picker({ slots, day, setDay, time, setTime }) {
  const days = Object.keys(slots);
  return (
    <>
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="booking-days">
        {days.map((d) => (
          <button key={d} type="button" onClick={() => { setDay(d); setTime(""); }} data-testid={`booking-day-${d}`}
            className={cn("shrink-0 px-3 py-2 rounded-lg border text-sm transition-colors", d === day ? "bg-emerald-700 border-emerald-700 text-white" : "border-slate-200 hover:border-emerald-600")}>{dayLabel(d)}</button>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2" data-testid="booking-times">
        {(slots[day] || []).map((t) => (
          <button key={t} type="button" onClick={() => setTime(t)} data-testid={`booking-time-${t.replace(":", "")}`}
            className={cn("py-1.5 rounded-md border text-sm transition-colors", t === time ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 hover:border-slate-900")}>{t}</button>
        ))}
      </div>
    </>
  );
}

export function BookViewing({ id }) {
  const { data, reload } = useFetch(`/public/listings/${id}/viewing-slots`, true);
  const slots = useMemo(() => data?.slots || {}, [data]);
  const [day, setDay] = useState("");
  const [time, setTime] = useState("");
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if ((!day || !slots[day]) && Object.keys(slots).length) { setDay(Object.keys(slots)[0]); }
  }, [slots, day]);
  if (!data?.enabled) return null;
  if (!Object.keys(slots).length) return <div className="panel text-sm text-slate-600" data-testid="booking-none"><h3 className="text-lg font-bold mb-1">Book a viewing</h3>No viewing times are free right now — send the agent an enquiry below and they'll arrange one.</div>;
  if (done) return <div className="panel text-sm" data-testid="booking-done"><CalendarCheck className="w-5 h-5 text-emerald-700 mb-2" />Viewing requested for <b>{dayLabel(done.slice(0, 10))} at {done.slice(11)}</b>. The agent will confirm by email.</div>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!time) return toast.error("Pick a time for your viewing");
    setBusy(true);
    try { const r = await api.post(`/public/listings/${id}/viewings`, { ...f, start: `${day}T${time}` }); setDone(r.data.start); }
    catch (err) { toast.error(errMsg(err)); if (err?.response?.status === 409) { setTime(""); reload(); } } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="panel space-y-3" data-testid="booking-form">
      <h3 className="text-lg font-bold">Book a viewing</h3>
      <p className="text-xs text-slate-500">Pick a time ({data.slot_minutes} min). The agent confirms every request.</p>
      <Picker slots={slots} day={day} setDay={setDay} time={time} setTime={setTime} />
      {time && <>
        <Input required placeholder="Your name" value={f.name} onChange={set("name")} data-testid="booking-name" />
        <Input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="booking-email" />
        <Input placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="booking-phone" />
        <Textarea rows={2} placeholder="Anything the agent should know? (optional)" value={f.message} onChange={set("message")} data-testid="booking-message" />
        <Button type="submit" disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="booking-submit">Request {dayLabel(day)} at {time}</Button>
      </>}
    </form>
  );
}

export function useRecordView(id) {
  useEffect(() => {
    const key = `pn_viewed_${id}_${new Date().toDateString()}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    api.post(`/public/listings/${id}/view`, { visitor: visitorId() }).catch(() => {});
  }, [id]);
}
